import { log } from './log'

// Team data source: ESPN Stats API (free, no key required).
// Base: https://site.api.espn.com/apis/site/v2/sports/<sportPath>
// Routes used here go through our Express proxy at /api/espn/<sport>/...
//   /teams                — list of teams (for ID lookup)
//   /teams/<teamId>       — team detail (record + statistics splits)
//   /standings            — conference standings
//
// Stats parsing: parseESPNStats() flattens the nested splits.categories[]
// shape into a { ppg, papg, rpg, ... } object keyed by what STAT_DEFS
// expects. ESPN's field names aren't consistent across sports, so each
// sport's pick list tries a few candidate names. New candidate names
// surfaced by the raw-data log at the top of getTeamStats can be added
// to the per-sport switch without touching anything else.

// ── Stat definitions per sport ────────────────────────────────────────────────
export const STAT_DEFS = {
  NBA: [
    { key:'ppg',   label:'Points Per Game',    better:'higher', fmt: v => v.toFixed(1) },
    { key:'papg',  label:'Pts Allowed / Game',  better:'lower',  fmt: v => v.toFixed(1) },
    { key:'rpg',   label:'Rebounds Per Game',   better:'higher', fmt: v => v.toFixed(1) },
    { key:'winPct',label:'Win %',               better:'higher', fmt: v => `${v.toFixed(1)}%` },
  ],
  NHL: [
    { key:'gpg',   label:'Goals Per Game',      better:'higher', fmt: v => v.toFixed(2) },
    { key:'gapg',  label:'Goals Against / Game',better:'lower',  fmt: v => v.toFixed(2) },
    { key:'ppPct', label:'Power Play %',         better:'higher', fmt: v => `${v.toFixed(1)}%` },
    { key:'winPct',label:'Win %',               better:'higher', fmt: v => `${v.toFixed(1)}%` },
  ],
  MLB: [
    { key:'avg',         label:'Batting Avg',  better:'higher', fmt: v => `.${String(Math.round(v*1000)).padStart(3,'0')}` },
    { key:'obp',         label:'On-Base %',    better:'higher', fmt: v => `.${String(Math.round(v*1000)).padStart(3,'0')}` },
    { key:'ops',         label:'OPS',          better:'higher', fmt: v => v.toFixed(3) },
    { key:'homeRuns',    label:'Home Runs',    better:'higher', fmt: v => Math.round(v).toString() },
    { key:'rbi',         label:'RBI',          better:'higher', fmt: v => Math.round(v).toString() },
    { key:'stolenBases', label:'Stolen Bases', better:'higher', fmt: v => Math.round(v).toString() },
    { key:'era',         label:'ERA',          better:'lower',  fmt: v => v.toFixed(2) },
    { key:'whip',        label:'WHIP',         better:'lower',  fmt: v => v.toFixed(2) },
    { key:'wins',        label:'Wins',         better:'higher', fmt: v => Math.round(v).toString() },
    { key:'saves',       label:'Saves',        better:'higher', fmt: v => Math.round(v).toString() },
    { key:'kPer9',       label:'K/9',          better:'higher', fmt: v => v.toFixed(1) },
    { key:'fieldingPct', label:'Fielding %',   better:'higher', fmt: v => `.${String(Math.round(v*1000)).padStart(3,'0')}` },
  ],
  NFL: [
    { key:'ppg',    label:'Points Per Game',    better:'higher', fmt: v => v.toFixed(1) },
    { key:'papg',   label:'Pts Allowed / Game', better:'lower',  fmt: v => v.toFixed(1) },
    { key:'passypg',label:'Pass Yds / Game',    better:'higher', fmt: v => Math.round(v).toString() },
    { key:'rushypg',label:'Rush Yds / Game',    better:'higher', fmt: v => Math.round(v).toString() },
    { key:'thirdPct',label:'3rd Down %',        better:'higher', fmt: v => `${v.toFixed(1)}%` },
    { key:'toDiff', label:'Turnover Diff',      better:'higher', fmt: v => v >= 0 ? `+${v}` : `${v}` },
  ],
  UFC: [
    { key:'SLpM',  label:'Sig. Strikes / Min',    better:'higher', fmt: v => v.toFixed(1) },
    { key:'strAcc',label:'Strike Accuracy',       better:'higher', fmt: v => `${v.toFixed(0)}%` },
    { key:'strDef',label:'Strike Defense',        better:'higher', fmt: v => `${v.toFixed(0)}%` },
    { key:'SApM',  label:'Sig. Strikes Absorbed', better:'lower',  fmt: v => v.toFixed(1) },
    { key:'tdAvg', label:'Takedowns / 15min',     better:'higher', fmt: v => v.toFixed(1) },
    { key:'tdAcc', label:'Takedown Accuracy',     better:'higher', fmt: v => `${v.toFixed(0)}%` },
    { key:'tdDef', label:'Takedown Defense',      better:'higher', fmt: v => `${v.toFixed(0)}%` },
    { key:'subAvg',label:'Submissions / 15min',   better:'higher', fmt: v => v.toFixed(1) },
  ],
}

// ── ESPN live data ────────────────────────────────────────────────────────────

const statsCache = {}
const CACHE_TIME = 60 * 60 * 1000 // 1 hour
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

async function fetchESPNTeamStats(teamName, sport) {
  const cacheKey = `${sport}_${teamName}`
  const now = Date.now()

  if (statsCache[cacheKey] && now - statsCache[cacheKey].time < CACHE_TIME) {
    return statsCache[cacheKey].data
  }

  const sportKey = sport.toLowerCase()

  try {
    const teamsData = await fetch(`${API_URL}/api/espn/${sportKey}/teams`).then(r => r.json())
    const teams = teamsData?.sports?.[0]?.leagues?.[0]?.teams || []

    const match = teams.find(t =>
      t.team.displayName === teamName ||
      t.team.shortDisplayName === teamName ||
      t.team.name === teamName ||
      t.team.displayName.includes(teamName) ||
      teamName.includes(t.team.name)
    )

    if (!match) {
      log('[teamStats] no team match for:', teamName, sport,
        '— searched', teams.length, 'teams')
      return null
    }

    const teamId   = match.team.id
    const teamData = await fetch(`${API_URL}/api/espn/${sportKey}/teams/${teamId}`).then(r => r.json())

    // record.items contains splits — total / home / road (or 'away' depending
    // on sport). ESPN already provides a formatted "W-L" string on each item's
    // summary field, so we don't need to recompute from individual stats.
    const recordItems = teamData?.team?.record?.items || []
    let overallRecord = null, homeRecord = null, awayRecord = null
    for (const item of recordItems) {
      const type = (item.type || item.description || '').toLowerCase()
      const summary = item.summary || ''
      if (!summary) continue
      if      (type === 'home')                         homeRecord    = summary
      else if (type === 'road' || type === 'away')      awayRecord    = summary
      else if (type === 'total' || type === 'overall' || type === '') overallRecord = overallRecord || summary
    }
    const record = overallRecord || recordItems[0]?.summary || ''
    let   stats  = teamData?.team?.statistics?.splits?.categories || []

    // Some teams have an empty statistics block on the main detail endpoint
    // — try the dedicated /statistics endpoint as a fallback. ESPN nests
    // the categories differently across endpoints, so we probe several
    // common shapes.
    if (stats.length === 0) {
      try {
        const statsData = await fetch(`${API_URL}/api/espn/${sportKey}/teams/${teamId}/statistics`).then(r => r.json())
        const alt = statsData?.splits?.categories
                 || statsData?.statistics?.splits?.categories
                 || statsData?.results?.stats?.splits?.categories
                 || statsData?.results?.stats?.categories   // MLB shape — no `splits` level
                 || []
        if (alt.length > 0) {
          stats = alt
        }
      } catch (err) {
        log('[teamStats] /statistics fallback failed for', teamName, err.message)
      }
    }

    // Recent form via the schedule endpoint — last 5 completed games, mapped
    // to W/L based on this team's `winner` flag in each competition. Best-
    // effort: a schedule failure shouldn't blow up the whole stats call.
    let form = []
    try {
      const schedule = await fetch(`${API_URL}/api/espn/${sportKey}/teams/${teamId}/schedule`).then(r => r.json())
      const events = schedule?.events || []
      const completed = events.filter(e => e.competitions?.[0]?.status?.type?.completed === true)
      form = completed.slice(-5).map(e => {
        const comp = e.competitions?.[0]
        const me   = comp?.competitors?.find(c =>
          String(c.team?.id) === String(teamId) || String(c.id) === String(teamId)
        )
        return me?.winner === true ? 'W' : 'L'
      })
    } catch (err) {
      log('[teamStats] schedule fetch failed for', teamName, err.message)
    }
    const streak = computeStreak(form)

    if (!record && (!stats || stats.length === 0)) {
      log('[teamStats] no stats found for:', teamName, sport,
        'ESPN response:', JSON.stringify(teamData).substring(0, 200))
    }

    const result = {
      record, sport, stats, teamId,
      displayName: match.team.displayName,
      homeRecord:  homeRecord || '',
      awayRecord:  awayRecord || '',
      form,
      streak,
    }
    statsCache[cacheKey] = { data: result, time: now }
    return result

  } catch (err) {
    log(`[teamStats] fetchESPNTeamStats failed for "${teamName}":`, err.message)
    return null
  }
}

// Computes a "W3" / "L2" style streak from a chronological form array.
// Counts consecutive matching results from the end of the array.
function computeStreak(form) {
  if (!Array.isArray(form) || form.length === 0) return ''
  const last = form[form.length - 1]
  let count = 0
  for (let i = form.length - 1; i >= 0; i--) {
    if (form[i] === last) count++
    else break
  }
  return `${last}${count}`
}

async function fetchESPNStandings(sport) {
  const cacheKey = `standings_${sport}`
  const now = Date.now()

  if (statsCache[cacheKey] && now - statsCache[cacheKey].time < CACHE_TIME) {
    return statsCache[cacheKey].data
  }

  try {
    const data = await fetch(`${API_URL}/api/espn/${sport.toLowerCase()}/standings`).then(r => r.json())

    const standings = {}
    const entries = data?.children?.flatMap(conf => conf.standings?.entries || []) || []
    entries.forEach(entry => {
      const name   = entry.team?.displayName
      const record = entry.stats?.find(s => s.name === 'overall')?.displayValue || ''
      if (name) standings[name] = record
    })

    statsCache[cacheKey] = { data: standings, time: now }
    return standings

  } catch (err) {
    log('ESPN standings fetch failed:', err)
    return {}
  }
}

// ── Safe number helpers ───────────────────────────────────────────────────────

export function safeNum(val, fallback = 0) {
  const n = parseFloat(val)
  return isNaN(n) ? fallback : n
}

export function safeStr(val, fallback = 'N/A') {
  if (val === null || val === undefined || val === '' || String(val) === 'NaN') return fallback
  return String(val)
}

// ── ESPN stats parser ────────────────────────────────────────────────────────
// Flattens ESPN's nested splits.categories[].stats[] into { name: number }
// then maps the values onto STAT_DEFS keys per sport. Returns numbers so the
// fmt callbacks in STAT_DEFS (v.toFixed(...), padStart, etc.) work directly.
function parseESPNStats(categories, sport) {
  if (!Array.isArray(categories) || categories.length === 0) return {}

  // Track both qualified (`<category>.<statName>`) and un-qualified
  // (`<statName>`) keys. Some stat names repeat across categories — MLB has
  // `homeRuns` and `strikeouts` in both batting and pitching, meaning the
  // wrong value wins under a flat-overwrite scheme depending on category
  // order. Qualified keys let the per-sport switch be explicit (e.g.
  // `batting.homeRuns` = HRs hit, not allowed). The un-qualified key is
  // first-writer-wins so existing pick lists for NBA/NHL/NFL keep working.
  const flat = {}
  for (const cat of categories) {
    const catName = (cat?.name || '').toLowerCase()
    for (const s of cat?.stats || []) {
      if (!s?.name) continue
      const numeric  = typeof s.value === 'number' ? s.value : parseFloat(s.value)
      const fallback = parseFloat(s.displayValue)
      const v = !isNaN(numeric) ? numeric : (!isNaN(fallback) ? fallback : null)
      if (v === null) continue
      if (catName) flat[`${catName}.${s.name}`] = v
      if (flat[s.name] === undefined) flat[s.name] = v
    }
  }

  // First non-null match across candidate ESPN field names.
  const pick = (...keys) => {
    for (const k of keys) if (flat[k] != null) return flat[k]
    return null
  }
  // ESPN sometimes returns percentages as 0-1, sometimes as 0-100. Normalize
  // to 0-100 since STAT_DEFS fmt callbacks expect that range.
  const pickPct = (...keys) => {
    const v = pick(...keys)
    if (v == null) return null
    return v <= 1 ? v * 100 : v
  }

  switch (sport) {
    case 'NBA':
      return {
        ppg:    pick('avgPoints', 'points', 'pointsPerGame', 'pts', 'scoring', 'avgPts'),
        papg:   pick('avgPointsAgainst', 'pointsAgainst', 'oppAvgPoints', 'defensivePoints', 'avgPointsAllowed', 'opponentPointsPerGame'),
        rpg:    pick('avgRebounds', 'rebounds', 'reboundsPerGame', 'totalRebounds', 'rebs'),
        winPct: pickPct('winPercent', 'winPercentage', 'winPct', 'gamesWonPct'),
      }
    case 'NHL':
      return {
        gpg:    pick('avgGoals', 'goalsPerGame', 'goals', 'avgGF', 'goalsFor', 'gf'),
        gapg:   pick('avgGoalsAgainst', 'goalsAgainst', 'avgGA', 'ga', 'goalsAllowed', 'goalsAgainstPerGame'),
        ppPct:  pickPct('powerPlayPct', 'powerPlayPercentage', 'powerPlay', 'PP%', 'powerPlayPercent'),
        winPct: pickPct('winPercent', 'winPercentage', 'winPct'),
      }
    case 'MLB':
      // ESPN returns batting averages as 0.276 (decimal form), which matches
      // what STAT_DEFS.MLB.avg/obp/fieldingPct fmt expects. Qualified keys
      // pinned to category so homeRuns/strikeouts pull from batting (HRs hit)
      // rather than getting clobbered by the same-name pitching stat.
      return {
        avg:         pick('batting.avg', 'avg'),
        obp:         pick('batting.onBasePct', 'onBasePct'),
        slg:         pick('batting.slugAvg', 'slugAvg'),
        ops:         pick('batting.OPS', 'OPS'),
        homeRuns:    pick('batting.homeRuns', 'homeRuns'),
        rbi:         pick('batting.RBIs', 'RBIs'),
        stolenBases: pick('batting.stolenBases', 'stolenBases'),
        era:         pick('pitching.ERA', 'ERA'),
        whip:        pick('pitching.WHIP', 'WHIP'),
        wins:        pick('pitching.wins', 'wins'),
        saves:       pick('pitching.saves', 'saves'),
        kPer9:       pick('pitching.strikeoutsPerNineInnings', 'strikeoutsPerNineInnings'),
        fieldingPct: pick('fielding.fieldingPct', 'fieldingPct'),
      }
    case 'NFL':
      return {
        ppg:      pick('avgPoints', 'points', 'pointsPerGame', 'pts', 'scoring'),
        papg:     pick('avgPointsAgainst', 'pointsAgainst', 'oppAvgPoints', 'avgPointsAllowed', 'opponentPointsPerGame'),
        passypg:  pick('passingYardsPerGame', 'avgPassingYards', 'netPassingYardsPerGame', 'avgNetPassingYards', 'passYards'),
        rushypg:  pick('rushingYardsPerGame', 'avgRushingYards', 'rushYards'),
        thirdPct: pickPct('thirdDownConvPct', 'thirdDownConvertedPct', 'thirdDownPct', 'thirdDownConversionPct'),
        toDiff:   pick('turnoverDifferential', 'turnoverDifference', 'turnoverDiff', 'turnoverRatio'),
      }
    default:
      return {}
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

// Returns null when ESPN doesn't provide usable data for this team. We do
// NOT fall back to hand-typed mock numbers — surfaces missing teams to the
// UI so the ESPN fetch path can be diagnosed and fixed instead of hidden.
export async function getTeamStats(teamName, sport) {
  const espnData = await fetchESPNTeamStats(teamName, sport)

  if (!espnData) {
    log('[teamStats] no stats found for:', teamName, sport, '— ESPN returned null')
    return null
  }

  const hasRecord = !!espnData.record
  const hasStats  = Array.isArray(espnData.stats) && espnData.stats.length > 0
  if (!hasRecord && !hasStats) {
    log('[teamStats] empty ESPN payload for:', teamName, sport)
    return null
  }

  return {
    sport:      sport || espnData.sport,
    record:     espnData.record    || '',
    form:       espnData.form      || [],
    streak:     espnData.streak    || '',
    homeRecord: espnData.homeRecord || '',
    awayRecord: espnData.awayRecord || '',
    stats:      parseESPNStats(espnData.stats, sport),
    injuries:   [],
    espnStats:  espnData.stats,
  }
}

// Historical H2H lookup retired with the mock H2H_DATA map. Keeping the
// export so callers (TeamComparison) don't have to be touched — they just
// stop rendering the section when this returns null.
export function getH2H(_team1, _team2) {
  return null
}

export function getStatDefs(sport) {
  return STAT_DEFS[sport] || STAT_DEFS.NBA
}

// ── UFC fighter stats ─────────────────────────────────────────────────────────
// ESPN MMA athlete endpoint returns bio fields (height/weight/reach/stance)
// plus a statistics array with categories of named stats (SLpM, strAcc, etc.).
// Names differ across endpoints — try a few candidates per field.
export async function fetchUFCFighterStats(athleteId, fighterName) {
  if (!athleteId) {
    log('[UFC stats] no athleteId for', fighterName)
    return null
  }
  try {
    const url = `${API_URL}/api/espn/ufc/athletes/${athleteId}`
    log('[UFC stats] fetching:', url)
    const res  = await fetch(url, { signal: AbortSignal.timeout(5000) })
    if (!res.ok) {
      log('[UFC stats] non-ok response for', fighterName, '— status', res.status)
      return null
    }
    const data = await res.json()
    log('[UFC stats] core API response:', fighterName, ':',
      JSON.stringify(data).substring(0, 500))
    const athlete = data?.athlete || data

    // Flatten every (statName -> displayValue) from every category so the
    // candidate-name lookup below can survive ESPN renaming things.
    const flat = {}
    const categories = athlete?.statistics?.[0]?.splits?.categories
                    || athlete?.statistics?.splits?.categories
                    || athlete?.statistics?.categories
                    || []
    for (const cat of categories) {
      for (const s of cat?.stats || []) {
        if (!s?.name) continue
        flat[s.name] = s.displayValue ?? s.value
      }
    }

    // Core API ships statistics as a $ref URL rather than embedded. When
    // nothing populated the flat map above, follow the ref via our proxy
    // so the numeric stat rows (SLpM, strAcc, etc.) actually populate.
    const statsRef = athlete?.statistics?.$ref || athlete?.statistics?.href || null
    if (statsRef && Object.keys(flat).length === 0) {
      try {
        const statsRes = await fetch(`${API_URL}/api/espn/ufc/athletes/${athleteId}/statistics`,
          { signal: AbortSignal.timeout(5000) })
        if (statsRes.ok) {
          const statsData = await statsRes.json()
          log('[UFC stats] statistics data for', fighterName, ':',
            JSON.stringify(statsData).substring(0, 400))
          const cats = statsData?.splits?.categories
                    || statsData?.categories
                    || statsData?.statistics?.splits?.categories
                    || []
          for (const cat of cats) {
            for (const s of cat?.stats || []) {
              if (!s?.name) continue
              flat[s.name] = s.displayValue ?? s.value
            }
          }
        } else {
          log('[UFC stats] statistics non-ok for', fighterName, '— status', statsRes.status)
        }
      } catch (e) {
        log('[UFC stats] stats fetch failed for', fighterName, ':', e.message)
      }
    }

    const num = (...names) => {
      for (const n of names) {
        const raw = flat[n]
        if (raw == null) continue
        const v = parseFloat(String(raw).replace('%', ''))
        if (!isNaN(v)) return v
      }
      return null
    }
    // Normalize 0-1 percentages to 0-100 for the STAT_DEFS fmt callbacks.
    const pct = (...names) => {
      const v = num(...names)
      if (v == null) return null
      return v <= 1 ? v * 100 : v
    }

    // Record: core API gives { wins, losses, draws } numeric;
    // site API gives displayValue string. Handle both.
    let record = ''
    if (athlete?.record && typeof athlete.record === 'object') {
      if (athlete.record.displayValue) {
        record = athlete.record.displayValue
      } else if (typeof athlete.record.wins === 'number') {
        const w = athlete.record.wins
        const l = athlete.record.losses || 0
        const d = athlete.record.draws  || 0
        record = d > 0 ? `${w}-${l}-${d}` : `${w}-${l}`
      }
    } else if (typeof athlete?.record === 'string') {
      record = athlete.record
    }
    if (!record) record = athlete?.displayRecord || ''

    // Core API ships height/weight/reach as inches/pounds integers; site API
    // ships pre-formatted strings on a display* field. Format integers into
    // the conventional 5'10" / 145 lbs / 73" form when the display version
    // isn't present.
    const formatHeight = h => {
      if (typeof h === 'number') return `${Math.floor(h / 12)}'${h % 12}"`
      return h || ''
    }
    const formatWeight = w => {
      if (typeof w === 'number') return `${w} lbs`
      return w || ''
    }
    const formatReach = r => {
      if (typeof r === 'number') return `${r}"`
      return r || ''
    }

    return {
      sport:       'UFC',
      name:        athlete?.fullName || athlete?.displayName || fighterName || '',
      nickname:    athlete?.nickname
                || athlete?.nicknames?.[0]?.value
                || (typeof athlete?.nicknames?.[0] === 'string' ? athlete.nicknames[0] : '')
                || '',
      record,
      age:         athlete?.age || null,
      height:      athlete?.displayHeight || formatHeight(athlete?.height),
      weight:      athlete?.displayWeight || formatWeight(athlete?.weight),
      reach:       athlete?.displayReach  || formatReach(athlete?.reach),
      stance:      athlete?.stance?.text  || athlete?.stance?.displayName || (typeof athlete?.stance === 'string' ? athlete.stance : ''),
      nationality: athlete?.flag?.alt     || athlete?.citizenship || '',
      // flagUrl + headshot intentionally dropped — refactor/owned-team-badges
      // moved every ESPN-CDN image off the render path. Nationality remains
      // as a text label; fighter identity carried by name badge.
      wins:        athlete?.record?.wins   ?? athlete?.wins   ?? null,
      losses:      athlete?.record?.losses ?? athlete?.losses ?? null,
      // TeamComparison's RecentForm/HeadToHead/InjuryReport read these and
      // expect arrays/strings even when they're empty — keep the shape stable.
      form:        [],
      streak:      '',
      homeRecord:  '',
      awayRecord:  '',
      injuries:    [],
      stats: {
        SLpM:   num('SLpM',  'sigStrikesLandedPerMinute'),
        strAcc: pct('strAcc','sigStrikingAccuracy', 'sigStrikeAccuracy'),
        SApM:   num('SApM',  'sigStrikesAbsorbedPerMinute'),
        strDef: pct('strDef','sigStrikeDefense'),
        tdAvg:  num('tdAvg', 'takedownAverage'),
        tdAcc:  pct('tdAcc', 'takedownAccuracy'),
        tdDef:  pct('tdDef', 'takedownDefense'),
        subAvg: num('subAvg','submissionAverage'),
      },
    }
  } catch (err) {
    log('[UFC stats] failed for', fighterName, err.message)
    return null
  }
}

export async function getTeamRecord(teamName, sport) {
  const standings = await fetchESPNStandings(sport)
  return standings[teamName] || ''
}
